import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { loanService, kycService } from '../../services/loanService';

// ── helpers ────────────────────────────────────────────────────────────────────

function fmtAmount(n) {
  if (n >= 1_000_000) return `₦${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `₦${(n / 1_000).toFixed(0)}K`;
  return `₦${n.toFixed(0)}`;
}

const STAGES = [
  [0,   'Analysing your account tier…'],
  [30,  'Checking eligible products…'],
  [62,  'Calculating your credit limit…'],
  [85,  'Finalising your offer…'],
  [100, 'Done!'],
];

function currentMessage(progress) {
  let msg = STAGES[0][1];
  for (const [threshold, label] of STAGES) {
    if (progress >= threshold) msg = label;
  }
  return msg;
}

const TYPE_ICONS = {
  personal:  '👤', business: '💼', mortgage: '🏠',
  auto: '🚗', education: '🎓', emergency: '⚡',
};

// ── keyframe injection ─────────────────────────────────────────────────────────

const CSS = `
@keyframes cc-pulse {
  0%, 100% { transform: scale(1);    box-shadow: 0 0 0 0 rgba(91,142,255,0.4); }
  50%       { transform: scale(1.08); box-shadow: 0 0 40px 12px rgba(91,142,255,0.22); }
}
@keyframes cc-spin {
  to { transform: rotate(360deg); }
}
@keyframes cc-fadeup {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes cc-msg {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;

// ── sub-components ─────────────────────────────────────────────────────────────

function CheckingView({ progress }) {
  const msg = currentMessage(progress);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '60vh', padding: '2rem',
      textAlign: 'center',
    }}>
      {/* Pulsing icon */}
      <div style={{
        width: 96, height: 96, borderRadius: '50%',
        background: 'linear-gradient(135deg, #5B8EFF 0%, #06C9FF 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '2.4rem',
        animation: 'cc-pulse 1.4s ease-in-out infinite',
        marginBottom: '2.5rem',
      }}>
        🧮
      </div>

      <h1 style={{
        fontSize: '1.75rem', fontWeight: 800,
        color: 'var(--text-1)', marginBottom: '0.6rem', lineHeight: 1.2,
      }}>
        Calculating your<br />credit limit
      </h1>

      {/* Animated stage label */}
      <p key={msg} style={{
        color: 'var(--text-2)', fontSize: '0.95rem', marginBottom: '2.5rem',
        animation: 'cc-msg 0.3s ease',
      }}>
        {msg}
      </p>

      {/* Progress bar */}
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{
          height: 10, background: 'var(--surface-2, rgba(255,255,255,0.06))',
          borderRadius: 99, overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.08)',
        }}>
          <div style={{
            height: '100%',
            width: `${progress}%`,
            background: 'linear-gradient(90deg, #5B8EFF 0%, #06C9FF 100%)',
            borderRadius: 99,
            transition: 'width 0.4s ease',
            boxShadow: '0 0 12px rgba(91,142,255,0.55)',
          }} />
        </div>

        <div style={{
          display: 'flex', justifyContent: 'space-between',
          marginTop: '0.6rem', fontSize: '0.75rem', color: 'var(--text-3)',
        }}>
          <span>Analysing</span>
          <span style={{ color: '#5B8EFF', fontWeight: 700 }}>{Math.round(progress)}%</span>
          <span>Complete</span>
        </div>
      </div>
    </div>
  );
}

function EmptyView({ navigate }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '60vh', textAlign: 'center',
      padding: '2rem', animation: 'cc-fadeup 0.5s ease',
    }}>
      <div style={{
        width: 80, height: 80, borderRadius: '50%',
        background: 'var(--subtle-bg)',
        border: '1px solid var(--border-bright)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '2rem', marginBottom: '1.5rem',
      }}>🔍</div>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-1)', marginBottom: '0.5rem' }}>
        No Products Available
      </h2>
      <p style={{ color: 'var(--text-2)', fontSize: '0.9rem', maxWidth: 360, lineHeight: 1.6, marginBottom: '2rem' }}>
        There are no loan products currently available for your account tier.
        Please check back later or contact support.
      </p>
      <button className="btn btn-ghost" onClick={() => navigate('/dashboard')}>
        ← Back to dashboard
      </button>
    </div>
  );
}

function ResultView({ product, tier, navigate }) {
  const maxAmount    = parseFloat(product.max_amount) || 0;
  const effectiveRate = parseFloat(product.effective_rate) || 0;
  const baseRate     = parseFloat(product.base_interest_rate) || 0;
  const hasDiscount  = effectiveRate < baseRate;
  const loanType     = product.loan_type || 'personal';
  const tierLabel    = tier ? tier[0].toUpperCase() + tier.slice(1) : 'Basic';

  // Count-up animation
  const [displayAmt, setDisplayAmt] = useState(0);
  const rafRef = useRef();
  useEffect(() => {
    const start = performance.now();
    const duration = 1300;
    function tick(now) {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayAmt(maxAmount * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [maxAmount]);

  return (
    <div style={{ maxWidth: 600, animation: 'cc-fadeup 0.55s ease' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          background: 'rgba(0,200,120,0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem',
        }}>✅</div>
        <div>
          <h1 style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-1)', margin: 0 }}>
            Credit Limit Found
          </h1>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-2)', margin: 0 }}>
            Based on your {tierLabel} account tier
          </p>
        </div>
      </div>

      {/* Credit limit hero card */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(91,142,255,0.15) 0%, rgba(255,255,255,0.03) 100%)',
        border: '1px solid rgba(91,142,255,0.25)',
        borderRadius: 'var(--radius-lg)',
        padding: '2rem',
        textAlign: 'center',
        marginBottom: '1.25rem',
      }}>
        <p style={{ fontSize: '0.7rem', letterSpacing: '0.12em', color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: '0.6rem' }}>
          Maximum Credit Limit
        </p>
        <div style={{
          fontSize: 'clamp(2.5rem, 8vw, 3.5rem)',
          fontWeight: 800,
          lineHeight: 1,
          background: 'linear-gradient(135deg, #5B8EFF 0%, #06C9FF 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          marginBottom: '0.75rem',
        }}>
          {fmtAmount(displayAmt)}
        </div>
        <span style={{
          display: 'inline-block',
          padding: '0.25rem 0.9rem',
          borderRadius: 99,
          background: 'rgba(91,142,255,0.14)',
          border: '1px solid rgba(91,142,255,0.3)',
          fontSize: '0.75rem', fontWeight: 700, color: '#7ea8ff',
        }}>
          {tierLabel} Tier
        </span>
      </div>

      {/* Product details card */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p style={{
          fontSize: '0.65rem', letterSpacing: '0.1em', color: 'var(--text-3)',
          textTransform: 'uppercase', marginBottom: '0.9rem', fontWeight: 600,
        }}>
          Recommended Product
        </p>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.9rem', marginBottom: hasDiscount ? '1rem' : '1.25rem' }}>
          {/* Icon */}
          <div style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: 'linear-gradient(135deg, #5B8EFF 0%, #06C9FF 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.2rem',
          }}>
            {TYPE_ICONS[loanType] || '🏦'}
          </div>

          {/* Name + type */}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-1)', marginBottom: '0.15rem' }}>
              {product.name}
            </div>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-2)' }}>
              {(loanType[0].toUpperCase() + loanType.slice(1)).replace(/_/g, ' ')}
            </div>
          </div>

          {/* Rate */}
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            {hasDiscount && (
              <div style={{
                fontSize: '0.75rem', color: 'var(--text-3)',
                textDecoration: 'line-through', marginBottom: '0.1rem',
              }}>
                {baseRate.toFixed(1)}%
              </div>
            )}
            <div style={{
              fontSize: '1.25rem', fontWeight: 800,
              background: 'linear-gradient(135deg, #5B8EFF 0%, #06C9FF 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              {effectiveRate.toFixed(1)}% p.a.
            </div>
          </div>
        </div>

        {/* Tier discount badge */}
        {hasDiscount && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
            padding: '0.35rem 0.75rem',
            background: 'rgba(0,200,120,0.1)',
            border: '1px solid rgba(0,200,120,0.25)',
            borderRadius: 99, fontSize: '0.78rem', color: '#00C878',
            fontWeight: 600, marginBottom: '1.25rem',
          }}>
            🏷️ {(baseRate - effectiveRate).toFixed(2)}% off for your tier
          </div>
        )}

        {/* Stats row */}
        <div style={{
          display: 'flex', gap: '1.5rem', paddingTop: '1rem',
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}>
          {[
            ['Min',    fmtAmount(parseFloat(product.min_amount) || 0)],
            ['Max',    fmtAmount(parseFloat(product.max_amount) || 0)],
            ['Tenure', `${product.min_tenure_months}–${product.max_tenure_months} mo`],
          ].map(([label, value]) => (
            <div key={label}>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', marginBottom: '0.2rem' }}>{label}</div>
              <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-1)' }}>{value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
        <button
          className="btn btn-primary btn-lg"
          style={{ flex: 1 }}
          onClick={() => navigate('/loans/apply', { state: { product } })}
        >
          Apply Now →
        </button>
        <button
          className="btn btn-ghost btn-lg"
          onClick={() => navigate('/dashboard')}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

const KYC_MESSAGES = {
  not_submitted: { icon: '🪪', title: 'KYC Required', body: 'Complete identity verification to apply for a loan.', cta: 'Complete KYC', ctaTo: '/profile' },
  pending:       { icon: '⏳', title: 'KYC Under Review', body: 'Your documents are being reviewed. You\'ll be notified once approved.', cta: null },
  under_review:  { icon: '🔍', title: 'KYC Being Verified', body: 'Our compliance team is reviewing your documents (1–2 business days).', cta: null },
  rejected:      { icon: '❌', title: 'KYC Rejected', body: 'Your verification was rejected. Please resubmit with correct documents.', cta: 'Resubmit KYC', ctaTo: '/profile' },
  resubmit:      { icon: '🔄', title: 'Resubmission Required', body: 'Additional information is needed. Please resubmit your KYC.', cta: 'Resubmit KYC', ctaTo: '/profile' },
};

export default function CreditCheck() {
  const navigate  = useNavigate();
  const [phase, setPhase]     = useState('kyc-check'); // 'kyc-check'|'checking'|'done'|'empty'|'kyc-gate'
  const [progress, setProgress] = useState(0);
  const [product, setProduct]   = useState(null);
  const [kycData, setKycData]   = useState(null);
  const [tier, setTier]         = useState('basic');

  useEffect(() => {
    let cancelled = false;

    async function run() {
      // 1. Check KYC first
      try {
        const kycRes  = await kycService.getStatus();
        const kyc     = kycRes.data.data;
        if (!cancelled) setKycData(kyc);

        if (kyc.status !== 'approved') {
          if (!cancelled) setPhase('kyc-gate');
          return;
        }
      } catch {
        if (!cancelled) setPhase('kyc-gate');
        return;
      }

      // 2. Fetch profile for tier
      try {
        const { default: api } = await import('../../services/api');
        const profileRes = await api.get('/auth/profile/');
        if (!cancelled) setTier((profileRes.data.data?.tier || 'basic').toLowerCase());
      } catch { /* use default 'basic' */ }

      // 3. Start checking phase
      if (!cancelled) setPhase('checking');

      // Animate to 85% over 3 s while API call runs
      const apiPromise = loanService.getProducts().then(r => r.data.data);

      const slowTimer = new Promise(resolve => {
        let p = 0;
        const interval = setInterval(() => {
          p = Math.min(p + 1.5, 85);
          if (!cancelled) setProgress(p);
          if (p >= 85) { clearInterval(interval); resolve(); }
        }, 50);
      });

      // Wait for both API and minimum 3s animation
      const [products] = await Promise.all([apiPromise, slowTimer]);

      if (cancelled) return;

      // Jump to 100%
      let p = 85;
      await new Promise(resolve => {
        const interval = setInterval(() => {
          p = Math.min(p + 3, 100);
          setProgress(p);
          if (p >= 100) { clearInterval(interval); resolve(); }
        }, 30);
      });

      await new Promise(r => setTimeout(r, 450));
      if (cancelled) return;

      // Find best product (highest max_amount)
      let best = null;
      if (Array.isArray(products) && products.length > 0) {
        best = products.reduce((a, b) =>
          (parseFloat(a.max_amount) || 0) >= (parseFloat(b.max_amount) || 0) ? a : b
        );
      }

      setProduct(best);
      setPhase(best ? 'done' : 'empty');
    }

    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── KYC gate ──
  if (phase === 'kyc-gate') {
    const status = kycData?.status || 'not_submitted';
    const info   = KYC_MESSAGES[status] || KYC_MESSAGES.not_submitted;
    return (
      <div style={{ maxWidth: 520 }}>
        <div className="page-header">
          <h1>Apply for a Loan</h1>
          <p>Verify your identity to get started</p>
        </div>
        <div style={{
          background: 'rgba(255,153,0,0.08)', border: '1px solid rgba(255,153,0,0.25)',
          borderRadius: 'var(--radius-lg)', padding: '2rem', textAlign: 'center',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{info.icon}</div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-1)', marginBottom: '0.5rem' }}>{info.title}</h2>
          <p style={{ color: 'var(--text-2)', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>{info.body}</p>
          {info.cta && (
            <button className="btn btn-primary btn-lg" onClick={() => navigate(info.ctaTo)}>{info.cta}</button>
          )}
          <div style={{ marginTop: '1.25rem' }}>
            <button className="btn btn-ghost" style={{ fontSize: '0.875rem' }} onClick={() => navigate('/dashboard')}>
              ← Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{CSS}</style>
      {phase === 'kyc-check' || phase === 'checking'
        ? <CheckingView progress={progress} />
        : phase === 'done'
          ? <ResultView product={product} tier={tier} navigate={navigate} />
          : <EmptyView navigate={navigate} />
      }
    </>
  );
}
